import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { isBefore } from 'date-fns';

import Swal, { SweetAlertIcon } from 'sweetalert2';
import fileDownload from 'js-file-download';

export const getOptionsFromEnum = <T extends Record<string, string | number>>(
  enumType: T
): { value: T[keyof T]; label: string }[] => {
  return Object.entries(enumType).map(([key, value]) => ({
    value: value as T[keyof T],
    label: key
  }));
};

export const WEB_SOURCE_INFO_CUTOFF = '2026-04-10';

export function getWebChannelSourceLabel(
  sourceInfo: string | undefined | null,
  createdAt: string | number | Date | undefined
): string {
  const trimmed = sourceInfo?.trim();
  if (trimmed && moment(createdAt).isAfter(WEB_SOURCE_INFO_CUTOFF, 'day')) {
    return trimmed;
  }
  return 'Website';
}

export const formatDate = (date: string | number | Date | undefined, formatStr: string = 'MMM D, YYYY hh:mm A') => {
  if (!date) return 'Invalid Date';
  const parsedDate = moment.parseZone(date); // this keeps the offset
  if (!parsedDate.isValid()) return 'Invalid Date';
  return parsedDate.format(formatStr);
};

/** Formats an absolute timestamp (e.g. ISO with Z) in the browser's local timezone. */
export const formatDateLocal = (
  date: string | number | Date | undefined,
  formatStr: string = 'MMM D, YYYY hh:mm A'
) => {
  if (!date) return 'Invalid Date';
  const parsedDate = moment(date);
  if (!parsedDate.isValid()) return 'Invalid Date';
  return parsedDate.format(formatStr);
};

export const formatDateByTimezone = (
  date: string | number | Date | undefined,
  timezone?: string | null,
  formatStr: string = 'MMM D, YYYY hh:mm A'
) => {
  if (!date) return 'Invalid Date';
  const parsedDate = moment.parseZone(date); // keeps offset if present
  if (!parsedDate.isValid()) return 'Invalid Date';

  if (timezone && moment.tz.zone(timezone)) {
    return parsedDate.tz(timezone).format(formatStr);
  }

  return parsedDate.format(formatStr);
};

export const setCookie = (name: string, value: any) => {
  Cookies.set(name, value, { expires: 15 });
};

export const getCookie = (name: string) => {
  return Cookies.get(name);
};

export const removeCookie = (name: string) => {
  Cookies.remove(name);
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = (error) => reject(error);
  });
};

export const isSubscriptionActive = (subscriptionEndDate?: string | null): boolean => {
  if (!subscriptionEndDate) return false;
  return isBefore(new Date(), subscriptionEndDate);
};

type ConfirmDialogProps = {
  title?: string;
  text?: string;
  icon?: SweetAlertIcon;
  iconColor?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
};

export const confirmDialog = async ({
  title = 'Are you sure?',
  text = 'Do you want to proceed?',
  icon = 'warning',
  iconColor = '',
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  confirmButtonColor = '#4B22A8',
  cancelButtonColor = 'transparent'
}: ConfirmDialogProps) => {
  const result = await Swal.fire({
    title,
    text,
    icon,
    iconColor,
    confirmButtonText,
    cancelButtonText,
    confirmButtonColor,
    cancelButtonColor,
    showCancelButton: true
  });

  return result;
};

export const getUrlInfo = () => {
  const url = window.location.href;
  const protocol = url.split('//')[0];
  const domain = url.split('//')[1].split(':')[0].split('/')[0];

  const domainParts = domain.split('.');
  const hasSubdomain = domainParts.length >= 2;
  const subdomain = hasSubdomain ? domainParts[0] : '';

  return {
    protocol,
    domain,
    subdomain
  };
};

export const normalizeWebsiteUrl = (url?: string) => {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl) return '';
  return /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
};

export const handleDownloadByUrl = async (fileUrl: string) => {
  try {
    const response = await fetch(fileUrl, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    link.setAttribute('download', fileUrl.split('/').pop() || 'downloaded_file');
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

export const fileDownloader = (data: any, filename = 'report.csv') => {
  fileDownload(data, filename);
};

export const formatCurrency = (value: number | undefined): string => {
  if (value == null || isNaN(value)) return '$0.00';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

export const formatDuration = (seconds: number) => {
  if (!seconds || seconds < 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    // Round minutes when showing hours
    const roundedMinutes = Math.round((seconds % 3600) / 60);
    return `${hours}h ${roundedMinutes}m`;
  }

  if (minutes > 0) {
    // Round seconds when showing minutes
    const roundedSeconds = Math.round(secs);
    return `${minutes}m ${roundedSeconds}s`;
  }

  return `${Math.round(secs)}s`;
};

export const formatLiveCallDuration = (seconds: number) => {
  if (!seconds || seconds < 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }

  return `${secs}s`;
};

export const formatRevenue = (cents: number) => {
  return cents ? `$${(cents / 100).toFixed(2)}` : '$0';
};

export const titleCase = (str: string) => {
  return str
    ?.replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()); // Capitalize each word
};

export function getLanguageNameByCode(code: string | null): string | undefined {
  if (code) {
    try {
      const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
      return displayNames.of(code) || code;
    } catch {
      return code; // fallback if Intl doesn't recognize it
    }
  }
}

export const formatDollars = (dollars?: string | number, decimalCount: number = 2) => {
  const value = Number(dollars) || 0;
  return decimalCount >= 9 ? `$${value}` : `$${value.toFixed(decimalCount)}`;
};

export const formatNumberDecimals = (value?: string | number, decimalCount: number = 2) => {
  value = Number(value) || 0;
  return `${value.toFixed(decimalCount)}`;
};

export const normalizeToUnderscore = (name: string): string => {
  return name.trim().replace(/\s+/g, '_').replace(/-/g, '_').replace(/_+/g, '_');
};

export const denormalizeFromUnderscore = (name: string): string => {
  return name.replace(/_+/g, ' ').trim();
};

export function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const units: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: 'year', seconds: 31536000 },
    { unit: 'month', seconds: 2592000 },
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 }
  ];

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  for (const { unit, seconds } of units) {
    const value = Math.floor(diffInSeconds / seconds);
    if (value >= 1) {
      return rtf.format(-value, unit);
    }
  }

  return 'just now';
}

export const getCharacterCount = (text: string | undefined | null): number => {
  return text ? text.length : 0;
};

export const formatCharacterCount = (current: number, max: number): string => {
  return `${current}/${max}`;
};

export const formatMilliseconds = (milliSeconds: number) => {
  if (!milliSeconds || milliSeconds < 0) return '0ms';
  const hours = Math.floor(milliSeconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliSeconds % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((milliSeconds % (1000 * 60)) / 1000);
  const milliseconds = Math.floor(milliSeconds % 1000);

  if (hours > 0) {
    const roundedMinutes = Math.round((milliSeconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${roundedMinutes}m`;
  }

  if (minutes > 0) {
    const roundedSeconds = Math.round((milliSeconds % (1000 * 60)) / 1000);
    return `${minutes}m ${roundedSeconds}s`;
  }

  if (seconds > 0) {
    return `${seconds}s ${milliseconds}ms`;
  }

  return `${milliseconds}ms`;
};

export const formatUnix = (ts: number) => {
  return new Date(ts * 1000).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export const formatUnixByTimezone = (
  tsSeconds: number | undefined | null,
  timezone?: string | null,
  formatStr: string = 'D MMM YYYY, h:mm A'
) => {
  if (!tsSeconds && tsSeconds !== 0) return 'Invalid Date';
  const parsedDate = moment.unix(tsSeconds);
  if (!parsedDate.isValid()) return 'Invalid Date';

  if (timezone && moment.tz.zone(timezone)) {
    return parsedDate.tz(timezone).format(formatStr);
  }

  return parsedDate.format(formatStr);
};

const formatHourDifference = (hours: number): string => {
  const rounded = Math.round(Math.abs(hours) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

export const getTimezoneOffsetLabel = (targetTimezone?: string | null): string | null => {
  if (!targetTimezone || !moment.tz.zone(targetTimezone)) return null;

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = moment();
  const targetOffsetMinutes = moment.tz(now, targetTimezone).utcOffset();
  const browserOffsetMinutes = moment.tz(now, browserTimezone).utcOffset();
  const diffHours = (targetOffsetMinutes - browserOffsetMinutes) / 60;

  if (diffHours === 0) return null;

  const formatted = formatHourDifference(diffHours);
  return diffHours > 0
    ? `${formatted} hrs ahead from selected location`
    : `${formatted} hrs behind from selected location`;
};
